/**
 * Video Consultation Room — WebRTC-based video & audio consultation with chat.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { consultationAPI } from '../../services/api';
import toast from 'react-hot-toast';

const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ]
};

/** Derive WebSocket base from the REST API URL so it always points at the backend. */
const getWsBase = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  return apiUrl.replace(/^http/, 'ws').replace(/\/api\/?$/, '');
};

const VideoConsultation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();
  const roomId = searchParams.get('room');
  const appointmentId = searchParams.get('appointment');

  const [status, setStatus] = useState('connecting'); // connecting | connected | ended
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [showChat, setShowChat] = useState(false);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnection = useRef(null);
  const localStream = useRef(null);
  const wsRef = useRef(null);
  const wsOpenedRef = useRef(false);
  const heartbeatRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pendingChatRef = useRef(null);
  const isConnectingRef = useRef(false);
  const statusRef = useRef('connecting');

  const updateStatus = useCallback((nextStatus) => {
    statusRef.current = nextStatus;
    setStatus(nextStatus);
  }, []);

  const setupMedia = useCallback(async () => {
    if (localStream.current) {
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = localStream.current;
      }
      return localStream.current;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
        audio: true,
      });
      localStream.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      return stream;
    } catch (err) {
      console.error('Media error:', err);
      toast.error('Unable to access camera/microphone. Check permissions.');
      return null;
    }
  }, []);

  const releaseMedia = useCallback(() => {
    // Stop all media tracks so the camera/mic indicator turns off
    if (localStream.current) {
      localStream.current.getTracks().forEach(track => track.stop());
      localStream.current = null;
    }
    // Detach stream from video elements (required for browser to release camera)
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
    // Close peer connection
    if (peerConnection.current) {
      peerConnection.current.onicecandidate = null;
      peerConnection.current.ontrack = null;
      peerConnection.current.onconnectionstatechange = null;
      peerConnection.current.close();
      peerConnection.current = null;
    }
    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    pendingChatRef.current = null;
    isConnectingRef.current = false;
    wsOpenedRef.current = false;
  }, []);

  const markCallEnded = useCallback((message) => {
    if (statusRef.current === 'ended') {
      return;
    }
    updateStatus('ended');
    releaseMedia();
    if (message) {
      toast(message);
    }
  }, [releaseMedia, updateStatus]);

  const bindPeerConnectionToSocket = useCallback((pc, ws) => {
    pc.onicecandidate = (event) => {
      if (event.candidate && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          type: 'ice-candidate',
          candidate: event.candidate,
        }));
      }
    };

    pc.ontrack = (event) => {
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
      updateStatus('connected');
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        markCallEnded('The consultation connection has ended.');
      }
    };
  }, [markCallEnded, updateStatus]);

  const createPeerConnection = useCallback((ws) => {
    const pc = new RTCPeerConnection(ICE_SERVERS);
    bindPeerConnectionToSocket(pc, ws);

    // Add local tracks
    if (localStream.current) {
      localStream.current.getTracks().forEach(track => {
        pc.addTrack(track, localStream.current);
      });
    }

    peerConnection.current = pc;
    return pc;
  }, [bindPeerConnectionToSocket]);

  const ensurePeerConnection = useCallback((ws) => {
    if (peerConnection.current) {
      bindPeerConnectionToSocket(peerConnection.current, ws);
      return peerConnection.current;
    }

    return createPeerConnection(ws);
  }, [bindPeerConnectionToSocket, createPeerConnection]);

  const sendOffer = useCallback(async (ws) => {
    const pc = peerConnection.current;
    if (!pc || !ws || ws.readyState !== WebSocket.OPEN) return;

    try {
      if (pc.signalingState === 'stable') {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
      } else if (pc.signalingState !== 'have-local-offer' || !pc.localDescription) {
        return;
      }

      ws.send(JSON.stringify({
        type: 'offer',
        offer: pc.localDescription,
      }));
    } catch (err) {
      console.warn('Offer send error:', err.message);
    }
  }, []);

  const sendChatMessage = useCallback((ws, message, sender) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;

    ws.send(JSON.stringify({
      type: 'chat',
      message,
      sender,
    }));

    return true;
  }, []);

  const connectWebSocket = useCallback(async (options = {}) => {
    if (!roomId) return;
    if (isConnectingRef.current) return;
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    isConnectingRef.current = true;
    wsOpenedRef.current = false;
    const isRetry = options.retry === true;

    try {
      await consultationAPI.join({ room_id: roomId });
    } catch (err) {
      isConnectingRef.current = false;
      if (isRetry) {
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket({ retry: true });
        }, 2000);
        return;
      }
      const message = err?.message || 'Unable to join consultation room.';
      toast.error(message);
      navigate(userRole === 'doctor' ? '/doctor/dashboard' : '/patient/dashboard');
      return;
    }

    const stream = await setupMedia();
    if (!stream) {
      isConnectingRef.current = false;
      return;
    }

    const wsUrl = `${getWsBase()}/ws/consultation/${roomId}/`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket connected');
      isConnectingRef.current = false;
      wsOpenedRef.current = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
      heartbeatRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 15000);
      const pc = ensurePeerConnection(ws);

      // If doctor, create and send offer now (in case patient is already in the room)
      if (userRole === 'doctor' && statusRef.current !== 'connected') {
        sendOffer(ws);
      }

      if (pendingChatRef.current) {
        const { message, sender } = pendingChatRef.current;
        if (sendChatMessage(ws, message, sender)) {
          pendingChatRef.current = null;
        }
      }
    };

    ws.onmessage = async (event) => {
      const data = JSON.parse(event.data);

      switch (data.type) {
        case 'pong':
          break;

        case 'offer':
          if (peerConnection.current) {
            // Only process offer when in stable state (guard against duplicate messages)
            if (peerConnection.current.signalingState !== 'stable') break;
            try {
              await peerConnection.current.setRemoteDescription(new RTCSessionDescription(data.offer));
              const answer = await peerConnection.current.createAnswer();
              await peerConnection.current.setLocalDescription(answer);
              ws.send(JSON.stringify({ type: 'answer', answer: answer }));
            } catch (err) {
              console.warn('Offer handling error (ignored):', err.message);
            }
          }
          break;

        case 'answer':
          if (peerConnection.current) {
            // Only process answer when waiting for remote answer
            if (peerConnection.current.signalingState !== 'have-local-offer') break;
            try {
              await peerConnection.current.setRemoteDescription(new RTCSessionDescription(data.answer));
            } catch (err) {
              console.warn('Answer handling error (ignored):', err.message);
            }
          }
          break;

        case 'ice-candidate':
          if (peerConnection.current && data.candidate) {
            try {
              await peerConnection.current.addIceCandidate(new RTCIceCandidate(data.candidate));
            } catch (err) {
              console.warn('ICE candidate error (ignored):', err.message);
            }
          }
          break;

        case 'user-joined':
          // The other party just connected — if we're the doctor, send the current offer
          if (userRole === 'doctor' && peerConnection.current) {
            await sendOffer(ws);
          }
          break;

        case 'user-left':
          // The other party disconnected
          toast('The other participant has left.', { icon: '⚠️' });
          break;

        case 'chat':
          setChatMessages(prev => [...prev, { sender: data.sender || 'Other', message: data.message, time: new Date() }]);
          break;

        case 'call-ended':
          markCallEnded('Consultation ended by the other party.');
          break;

        default:
          break;
      }
    };

    ws.onclose = () => {
      console.log('WebSocket closed');
      if (wsRef.current === ws) {
        wsRef.current = null;
      }
      isConnectingRef.current = false;
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      if (!wsOpenedRef.current) {
        toast.error('Connection error. Please retry.');
      } else if (statusRef.current !== 'ended' && !reconnectTimeoutRef.current) {
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket({ retry: true });
        }, 1000);
      }
    };

    ws.onerror = (err) => {
      console.error('WebSocket error:', err);
    };
  }, [roomId, userRole, setupMedia, ensurePeerConnection, navigate, sendOffer, sendChatMessage, markCallEnded]);

  useEffect(() => {
    // If doctor starting consultation, create room first
    const initConsultation = async () => {
      if (appointmentId && userRole === 'doctor' && !roomId) {
        try {
          const res = await consultationAPI.create({ appointment_id: appointmentId });
          const newRoomId = res.data.room_id;
          navigate(`/consultation?room=${newRoomId}&appointment=${appointmentId}`, { replace: true });
        } catch (err) {
          toast.error(err?.message || 'Failed to create consultation room.');
          navigate(-1);
        }
      } else {
        connectWebSocket();
      }
    };
    initConsultation();

    return () => {
      // Cleanup on unmount (e.g. navigating away without pressing End Call)
      releaseMedia();
    };
  }, [appointmentId, roomId, userRole, navigate, connectWebSocket, releaseMedia]);

  const toggleMute = () => {
    if (localStream.current) {
      localStream.current.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);
    }
  };

  const toggleVideo = () => {
    if (localStream.current) {
      localStream.current.getVideoTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsVideoOff(!isVideoOff);
    }
  };

  const endCall = useCallback(async () => {
    // Notify the other party before releasing media
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'end-call' }));
    }

    const shouldCompleteConsultation = statusRef.current === 'connected';

    if (userRole === 'doctor' && roomId) {
      try {
        await consultationAPI.end({
          room_id: roomId,
          notes: '',
          complete_consultation: shouldCompleteConsultation,
        });
        if (!shouldCompleteConsultation) {
          toast('Call ended before the patient connected. The appointment stays in the queue.', { icon: 'ℹ️' });
        }
      } catch (err) {
        toast.error(err?.message || 'Unable to finalize this consultation.');
      }
    }

    updateStatus('ended');
    releaseMedia();
  }, [releaseMedia, roomId, updateStatus, userRole]);

  const sendChat = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    const message = chatInput.trim();
    const sender = currentUser?.displayName || userRole;

    if (!wsRef.current) {
      pendingChatRef.current = { message, sender };
      connectWebSocket({ retry: true });
      toast('Reconnecting chat…', { icon: '⏳' });
      return;
    }

    if (wsRef.current.readyState !== WebSocket.OPEN) {
      pendingChatRef.current = { message, sender };
      connectWebSocket({ retry: true });
      toast('Reconnecting chat…', { icon: '⏳' });
      return;
    }

    sendChatMessage(wsRef.current, message, sender);
    setChatMessages(prev => [...prev, { sender: 'You', message, time: new Date() }]);
    setChatInput('');
  };

  if (status === 'ended') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-6">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Consultation Ended</h2>
          <p className="text-gray-500 text-sm mb-6">The video consultation has ended.</p>
          <div className="space-y-3">
            {userRole === 'doctor' && (
              <button
                onClick={() => navigate(`/doctor/prescriptions/write?appointment=${appointmentId || ''}`)}
                className="w-full py-3 rounded-xl font-semibold text-white bg-accent-500 hover:bg-accent-600 transition-colors"
              >
                Write Prescription
              </button>
            )}
            <button
              onClick={() => navigate(userRole === 'doctor' ? '/doctor/dashboard' : '/patient/dashboard')}
              className="btn-secondary w-full"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Video Area */}
      <div className="flex-1 relative">
        {/* Remote video (full screen) */}
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className="w-full h-full object-cover"
        />

        {/* Connecting overlay */}
        {status === 'connecting' && (
          <div className="absolute inset-0 bg-gray-900 flex flex-col items-center justify-center text-white">
            <div className="spinner w-12 h-12 border-3 border-white mb-4"></div>
            <p className="text-lg font-medium">Connecting...</p>
            <p className="text-sm text-gray-400 mt-1">Setting up secure video consultation</p>
          </div>
        )}

        {/* Local video (small pip) */}
        <div className="absolute top-4 right-4 w-28 h-36 rounded-xl overflow-hidden bg-gray-800 shadow-lg border-2 border-white/20">
          <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
          {isVideoOff && (
            <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
              <span className="text-white text-xs">Camera Off</span>
            </div>
          )}
        </div>

        {/* Status badge */}
        <div className="absolute top-4 left-4">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
            status === 'connected' ? 'bg-green-500/80 text-white' : 'bg-yellow-500/80 text-white'
          }`}>
            <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
            {status === 'connected' ? 'Connected' : 'Connecting...'}
          </div>
        </div>
      </div>

      {/* Chat Panel */}
      {showChat && (
        <div className="absolute bottom-24 left-0 right-0 bg-black/80 backdrop-blur-sm mx-4 rounded-2xl max-h-64 flex flex-col">
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {chatMessages.map((msg, idx) => (
              <div key={idx} className={`text-sm ${msg.sender === 'You' ? 'text-right' : ''}`}>
                <span className="text-xs text-gray-400">{msg.sender}</span>
                <p className={`inline-block px-3 py-1.5 rounded-xl mt-0.5 ${
                  msg.sender === 'You' ? 'bg-primary-500 text-white' : 'bg-gray-700 text-white'
                }`}>
                  {msg.message}
                </p>
              </div>
            ))}
          </div>
          <form onSubmit={sendChat} className="p-3 border-t border-white/10 flex gap-2">
            <input
              type="text"
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              className="flex-1 bg-gray-700 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Type a message..."
            />
            <button type="submit" className="px-4 py-2 bg-primary-500 text-white rounded-xl text-sm font-medium">
              Send
            </button>
          </form>
        </div>
      )}

      {/* Controls Bar */}
      <div className="bg-gray-900/95 backdrop-blur-sm px-6 py-4 safe-area-bottom">
        <div className="flex items-center justify-center gap-4">
          {/* Mute */}
          <button
            onClick={toggleMute}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
              isMuted ? 'bg-red-500 text-white' : 'bg-gray-700 text-white hover:bg-gray-600'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMuted ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              )}
            </svg>
          </button>

          {/* Video toggle */}
          <button
            onClick={toggleVideo}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
              isVideoOff ? 'bg-red-500 text-white' : 'bg-gray-700 text-white hover:bg-gray-600'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isVideoOff ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              )}
            </svg>
          </button>

          {/* Chat */}
          <button
            onClick={() => setShowChat(!showChat)}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
              showChat ? 'bg-primary-500 text-white' : 'bg-gray-700 text-white hover:bg-gray-600'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </button>

          {/* End Call */}
          <button
            onClick={endCall}
            className="w-14 h-14 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoConsultation;
